/**
 * Production API Implementation of IVisitorRepository
 * Encapsulates all Axios / HTTP client communications with cancellation support.
 */
import { httpClient } from '../../../infrastructure/api';
import { logger } from '../../../infrastructure/logging';
import {
  VisitorType,
  VisitorPurpose,
  VisitorApprovalStatus,
  VisitorEntry,
  VisitorEntryRequest,
  PreApproveGuestRequest,
  VisitorDashboardStats
} from '../types/visitor';
import { ApiPageResponse } from '../../../types';
import { IVisitorRepository, IVisitorFilterParams } from './IVisitorRepository';

export class ApiVisitorRepository implements IVisitorRepository {
  async getTypes(signal?: AbortSignal): Promise<VisitorType[]> {
    try {
      const response = await httpClient.get('/visitors/types', { signal });
      return response.data?.data ?? response.data ?? [];
    } catch (err) {
      if ((err as any)?.name === 'CanceledError' || (err as any)?.code === 'ERR_CANCELED') throw err;
      logger.error('Failed to fetch visitor types', err);
      throw err;
    }
  }

  async getPurposes(signal?: AbortSignal): Promise<VisitorPurpose[]> {
    try {
      const response = await httpClient.get('/visitors/purposes', { signal });
      return response.data?.data ?? response.data ?? [];
    } catch (err) {
      if ((err as any)?.name === 'CanceledError' || (err as any)?.code === 'ERR_CANCELED') throw err;
      logger.error('Failed to fetch visitor purposes', err);
      throw err;
    }
  }

  async getStatuses(signal?: AbortSignal): Promise<VisitorApprovalStatus[]> {
    try {
      const response = await httpClient.get('/visitors/statuses', { signal });
      return response.data?.data ?? response.data ?? [];
    } catch (err) {
      if ((err as any)?.name === 'CanceledError' || (err as any)?.code === 'ERR_CANCELED') throw err;
      logger.error('Failed to fetch visitor statuses', err);
      throw err;
    }
  }

  async logEntry(request: VisitorEntryRequest, signal?: AbortSignal): Promise<VisitorEntry> {
    try {
      const response = await httpClient.post('/visitors/entries', request, { signal });
      return response.data?.data ?? response.data;
    } catch (err) {
      logger.error('Failed to log visitor entry', err);
      throw err;
    }
  }

  async approveEntry(entryId: number, memberId?: number, signal?: AbortSignal): Promise<VisitorEntry> {
    try {
      const response = await httpClient.post(
        `/visitors/entries/${entryId}/approve`,
        { action: 'APPROVE', memberId },
        { signal }
      );
      return response.data?.data ?? response.data;
    } catch (err) {
      logger.error(`Failed to approve visitor entry #${entryId}`, err);
      throw err;
    }
  }

  async rejectEntry(entryId: number, reason?: string, memberId?: number, signal?: AbortSignal): Promise<VisitorEntry> {
    try {
      const response = await httpClient.post(
        `/visitors/entries/${entryId}/approve`,
        { action: 'REJECT', reason, memberId },
        { signal }
      );
      return response.data?.data ?? response.data;
    } catch (err) {
      logger.error(`Failed to reject visitor entry #${entryId}`, err);
      throw err;
    }
  }

  async preApproveGuest(request: PreApproveGuestRequest, signal?: AbortSignal): Promise<VisitorEntry> {
    try {
      const response = await httpClient.post('/visitors/pre-approve', request, { signal });
      return response.data?.data ?? response.data;
    } catch (err) {
      logger.error('Failed to pre-approve guest', err);
      throw err;
    }
  }

  async checkoutVisitor(entryId: number, signal?: AbortSignal): Promise<VisitorEntry> {
    try {
      const response = await httpClient.post(`/visitors/entries/${entryId}/checkout`, {}, { signal });
      return response.data?.data ?? response.data;
    } catch (err) {
      logger.error(`Failed to checkout visitor #${entryId}`, err);
      throw err;
    }
  }

  async getEntries(
    societyId: number,
    page: number = 0,
    size: number = 10,
    filters?: IVisitorFilterParams,
    signal?: AbortSignal
  ): Promise<ApiPageResponse<VisitorEntry>> {
    try {
      const params: Record<string, any> = { page, size };
      if (filters?.date) params.date = filters.date;
      if (filters?.statusId) params.statusId = filters.statusId;
      if (filters?.typeId) params.typeId = filters.typeId;
      if (filters?.unitNumber) params.unitNumber = filters.unitNumber;
      if (filters?.search) params.search = filters.search;

      const response = await httpClient.get(`/societies/${societyId}/visitors`, { params, signal });
      return response.data?.data ?? response.data;
    } catch (err) {
      if ((err as any)?.name === 'CanceledError' || (err as any)?.code === 'ERR_CANCELED') throw err;
      logger.error('Failed to fetch visitor entries', err);
      throw err;
    }
  }

  async getStats(societyId: number, signal?: AbortSignal): Promise<VisitorDashboardStats> {
    try {
      const response = await httpClient.get(`/societies/${societyId}/visitors/stats`, { signal });
      return response.data?.data ?? response.data;
    } catch (err) {
      if ((err as any)?.name === 'CanceledError' || (err as any)?.code === 'ERR_CANCELED') throw err;
      logger.error('Failed to fetch visitor stats', err);
      throw err;
    }
  }
}
